import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  ArrowLeft,
  Flame,
  Trophy,
  Target,
  Gift,
  TrendingUp,
  FileText,
  BarChart3,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import ReconciliationView from '../../components/parent/ReconciliationView'
import SpellingProgress from '../../components/parent/SpellingProgress'

interface StudentProfile {
  student: { id: number; name: string; canvas_id: number }
  profile: {
    level: number
    title: string
    total_xp: number
    xp_in_level: number
    xp_for_next: number
    progress_pct: number
    study_streak: number
    longest_streak: number
    streak_shields: number
    total_achievements: number
    total_challenges_completed: number
  }
  recent_xp: Array<{
    event_type: string
    xp_amount: number
    description: string
    created_at: string
  }>
  goals: Array<{
    id: number
    title: string
    goal_type: string
    target_value: number
    current_value: number
    progress_pct: number
    status: string
    deadline: string | null
    custom_reward: string | null
  }>
  achievements: Array<{
    code: string
    name: string
    category: string
    tier: string
    earned_at: string
  }>
}

export default function ParentStudentProgress() {
  const { studentId } = useParams()

  const { data: profile, isLoading } = useQuery<StudentProfile>({
    queryKey: ['parent-student', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}`)
      return response.data
    },
    enabled: !!studentId,
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="h-32 bg-surface-card rounded-xl"></div>
        <div className="h-64 bg-surface-card rounded-xl"></div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">Student not found</p>
        <Link to="/parent" className="btn btn-primary mt-4">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/parent/student/${studentId}`} className="p-2 hover:bg-surface-hover rounded-lg">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{profile.student.name}</h1>
          <p className="text-gray-400 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-quest-accent" />
            Quest Progress & Achievements
          </p>
        </div>
        <div className="text-right">
          <div className="level-badge text-xl">{profile.profile.level}</div>
          <p className="text-sm text-gray-400 mt-1">{profile.profile.title}</p>
        </div>
      </div>

      {/* XP Progress Card */}
      <div className="card">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-quest-accent" />
          Level Progress
        </h2>
        <div className="mb-2 flex justify-between text-sm">
          <span className="text-gray-400">
            {profile.profile.xp_in_level.toLocaleString()} / {profile.profile.xp_for_next.toLocaleString()} XP
          </span>
          <span className="text-quest-accent font-medium">
            {profile.profile.total_xp.toLocaleString()} Total XP
          </span>
        </div>
        <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-quest-primary to-quest-secondary transition-all duration-500"
            style={{ width: `${profile.profile.progress_pct}%` }}
          />
        </div>
        <p className="text-sm text-gray-500 mt-2 text-center">
          {Math.round(profile.profile.progress_pct)}% to Level {profile.profile.level + 1}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <Flame
            className={`w-8 h-8 mx-auto mb-2 ${
              profile.profile.study_streak > 0 ? 'text-orange-500' : 'text-gray-500'
            }`}
          />
          <p className="text-2xl font-bold">{profile.profile.study_streak}</p>
          <p className="text-xs text-gray-500">Day Streak</p>
        </div>
        <div className="card text-center">
          <Trophy className="w-8 h-8 mx-auto mb-2 text-quest-accent" />
          <p className="text-2xl font-bold">{profile.profile.total_achievements}</p>
          <p className="text-xs text-gray-500">Achievements</p>
        </div>
        <div className="card text-center">
          <Target className="w-8 h-8 mx-auto mb-2 text-quest-primary" />
          <p className="text-2xl font-bold">{profile.profile.total_challenges_completed}</p>
          <p className="text-xs text-gray-500">Challenges</p>
        </div>
        <div className="card text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="text-2xl font-bold">{profile.profile.longest_streak}</p>
          <p className="text-xs text-gray-500">Best Streak</p>
        </div>
      </div>

      {/* Active Goals */}
      {profile.goals.filter((g) => g.status === 'active').length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-quest-primary" />
            Active Goals
          </h2>
          <div className="space-y-4">
            {profile.goals
              .filter((g) => g.status === 'active')
              .map((goal) => (
                <div key={goal.id} className="p-4 bg-surface-card/50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">{goal.title}</span>
                    <span className="text-sm text-quest-accent">
                      {Math.round(goal.progress_pct)}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full bg-gradient-to-r from-quest-primary to-quest-accent"
                      style={{ width: `${goal.progress_pct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>
                      {goal.current_value} / {goal.target_value}
                    </span>
                    {goal.custom_reward && (
                      <span className="flex items-center gap-1">
                        <Gift className="w-4 h-4" />
                        {goal.custom_reward}
                      </span>
                    )}
                  </div>
                </div>
              ))}
          </div>
          <Link
            to="/parent/courses"
            className="btn btn-secondary w-full mt-4"
          >
            Manage Goals
          </Link>
        </div>
      )}

      {/* Recent XP Activity */}
      {profile.recent_xp.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Recent XP Activity
          </h2>
          <div className="space-y-3">
            {profile.recent_xp.slice(0, 10).map((event, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 border-b border-gray-700/50 last:border-0"
              >
                <div>
                  <p className="text-sm">{event.description || event.event_type}</p>
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                  </p>
                </div>
                <span className="text-quest-accent font-bold">+{event.xp_amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Achievements */}
      {profile.achievements.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-quest-accent" />
            Recent Achievements
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {profile.achievements.slice(0, 8).map((achievement) => (
              <div
                key={achievement.code}
                className={`p-3 rounded-lg border text-center ${
                  achievement.tier === 'gold'
                    ? 'border-tier-gold bg-tier-gold/10'
                    : achievement.tier === 'silver'
                      ? 'border-tier-silver bg-tier-silver/10'
                      : 'border-tier-bronze bg-tier-bronze/10'
                }`}
              >
                <p className="font-medium text-sm">{achievement.name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDistanceToNow(new Date(achievement.earned_at), { addSuffix: true })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spelling Progress */}
      <SpellingProgress
        studentId={Number(studentId)}
        studentName={profile.student.name}
      />

      {/* Work Reconciliation */}
      <ReconciliationView
        studentId={Number(studentId)}
        studentName={profile.student.name}
      />

      {/* Bottom Navigation Tabs */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface-base border-t border-gray-800 p-2">
        <div className="max-w-4xl mx-auto flex justify-around">
          <Link
            to={`/parent/student/${studentId}`}
            className="flex flex-col items-center gap-1 p-2 text-gray-400 hover:text-gray-200"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs">Homework</span>
          </Link>
          <Link
            to={`/parent/student/${studentId}/scans`}
            className="flex flex-col items-center gap-1 p-2 text-gray-400 hover:text-gray-200"
          >
            <BarChart3 className="w-5 h-5" />
            <span className="text-xs">Scans</span>
          </Link>
          <Link
            to={`/parent/student/${studentId}/progress`}
            className="flex flex-col items-center gap-1 p-2 text-quest-primary"
          >
            <Trophy className="w-5 h-5" />
            <span className="text-xs">Progress</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
